import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { saveAs } from 'file-saver';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import authService from '../../api-authorization/AuthorizeService';
import { Link} from 'react-router-dom'

const OnlineGameDownloader = () => {
    const [playerName, setPlayerName] = useState('');
    const [lichessUsername, setLichessUsername] = useState('');
    const [chessComId, setChessComId] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [isSupporter, setIsSupporter] = useState(false);

    const GetMySupporterStatus = async (token) => {
        var headers = {
            headers: { 'Authorization': `Bearer ${token}` }
        }
        axios.get('/api/NACHUsers/GetMySupporterStatusExpDateUtc', headers).then((response) => {
            setIsSupporter(response.data.isSupporter);
            return response.data.isSupporter;
        }).catch((error) => {
            console.log(JSON.stringify(error));
            setIsSupporter(false);
            return false;
        });
    };

    useEffect(async () => {
        var token = await authService.getAccessToken();
        await GetMySupporterStatus(token);
    }, []);

    const handlePlayerNameChange = (event) => {
        setPlayerName(event.target.value);
    };

    const handleLichessUsernameChange = (event) => {
        setLichessUsername(event.target.value);
    };

    const handleChessComIdChange = (event) => {
        setChessComId(event.target.value);
    };

    const handleDownload = async () => {
        try {
            if (!isSupporter) {
                setErrorMessage('This is a support only feature. ');
                setOpenDialog(true);
                return;
            }
            // Validate input
            if (!playerName) {
                setErrorMessage('Player Name is required.');
                setOpenDialog(true);
                return;
            }

            if (!lichessUsername && !chessComId) {
                setErrorMessage('Either Lichess ID or Chess.com ID is required.');
                setOpenDialog(true);
                return;
            }

            // Download games based on input
            if (lichessUsername && !chessComId) {
                setLoading(true);
                const lichessResponse = await fetch(`https://lichess.org/api/games/user/${lichessUsername}`);
                const lichessData = await lichessResponse.text();
                if (lichessData.includes('DOCTYPE html')) {
                    setErrorMessage(`User ${lichessUsername} not found on Lichess.`);
                    setOpenDialog(true);
                    setLoading(false);
                    return;
                }
                if (lichessData.includes('Invalid user ID')) {
                    setErrorMessage('Invalid Lichess ID.');
                    setOpenDialog(true);
                    setLoading(false);
                    return;
                }
                const replacedPgn = lichessData.replace(new RegExp(lichessUsername, 'gi'), playerName);
                const blob = new Blob([replacedPgn], { type: 'text/plain' });
                saveAs(blob, `${playerName + "_lichess_"+lichessUsername}_games.pgn`);
                setLoading(false);
                return;
            }

            if (!lichessUsername && chessComId) {
                setLoading(true);
                const chessComResponse = await fetch(`https://api.chess.com/pub/player/${chessComId}/games/archives`);
                const chessComData = await chessComResponse.json();
                const archives = chessComData.archives || [];
                if (archives.length === 0) {
                    setErrorMessage(`No games found for Chess.com user ${chessComId}.`);
                    setOpenDialog(true);
                    setLoading(false);
                    return;
                }
                const playerGames = [];
                for (const monthlyArchive of archives) {
                    const monthlyResponse = await fetch(monthlyArchive);
                    const monthlyData = await monthlyResponse.json();
                    const games = monthlyData.games || [];
                    playerGames.push(...games);
                }
                if (playerGames.length > 0) {
                    const pgnContent = playerGames
                        .filter((game) => game.rules === 'chess')
                        .map((game) => {
                            return game.pgn.replace(new RegExp(chessComId, 'gi'), playerName);
                        })
                        .join('\n\n');
                    const blob = new Blob([pgnContent], { type: 'text/plain' });
                    saveAs(blob, `${playerName+"_chess.com_" + chessComId}_games.pgn`);
                    setLoading(false);
                    return;
                }
            }

            if (lichessUsername && chessComId) {
                setLoading(true);
                const lichessResponse = await fetch(`https://lichess.org/api/games/user/${lichessUsername}`);
                const lichessData = await lichessResponse.text();
                if (lichessData.includes('DOCTYPE html')) {
                    setErrorMessage(`User ${lichessUsername} not found on Lichess.`);
                    setOpenDialog(true);
                    setLoading(false);
                    return;
                }
                if (lichessData.includes('Invalid user ID')) {
                    setErrorMessage('Invalid Lichess ID.');
                    setOpenDialog(true);
                    setLoading(false);
                    return;
                }
                const lichessReplacedPgn = lichessData.replace(new RegExp(lichessUsername, 'gi'), playerName);

                const chessComResponse = await fetch(`https://api.chess.com/pub/player/${chessComId}/games/archives`);
                const chessComData = await chessComResponse.json();
                const archives = chessComData.archives || [];
                if (archives.length === 0) {
                    setErrorMessage(`No games found for Chess.com user ${chessComId}.`);
                    setOpenDialog(true);
                    setLoading(false);
                    return;
                }
                const playerGames = [];
                for (const monthlyArchive of archives) {
                    const monthlyResponse = await fetch(monthlyArchive);
                    const monthlyData = await monthlyResponse.json();
                    const games = monthlyData.games || [];
                    playerGames.push(...games);
                }
                if (playerGames.length > 0) {
                    const chessComPgnContent = playerGames
                        .filter((game) => game.rules === 'chess')
                        .map((game) => {
                            return game.pgn.replace(new RegExp(chessComId, 'gi'), playerName);
                        })
                        .join('\n\n');
                    const mergedPgnContent = `${lichessReplacedPgn}\n\n${chessComPgnContent}`;
                    const blob = new Blob([mergedPgnContent], { type: 'text/plain' });
                    saveAs(blob, `${playerName + "_chess.com_" + chessComId + "_lichess_" + lichessUsername}_games.pgn`);
                    setLoading(false);
                    return;
                }
            }
        } catch (error) {
            console.error('Error:', error.message);
            setErrorMessage('An error occurred. Please try again.');
            setOpenDialog(true);
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <Box sx={{ width: '100%', mt: 2, mb: 1, }}>
        <div className="Container" style={{ position: 'relative' }}>
            <Accordion m={2} pt={3} pl={3} pr={3} defaultExpanded={true}>
                <AccordionSummary aria-controls="event-info" id="event-info" expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#1976d2' }}>
                    <Typography color='white'>Download Player's Online Games</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ pt: 2, pb: 2 }}>
                        <Grid container direction="row" rowSpacing={1} columnSpacing={1} alignItems="center">
                            <Grid item xs={12} lg={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    label="Player Name"
                                    variant="outlined"
                                    margin="normal"
                                    value={playerName}
                                    onChange={handlePlayerNameChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} lg={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    label={
                                        <span>
                                            <a href={'https://chess.com'} target='_blank' rel='noreferrer'>
                                                Chess.com
                                            </a>&nbsp;
                                            <a href={'https://docs.google.com/spreadsheets/d/1c0GRuLXw-OUB5mJR_OLGKuvJqJw2UU2Kaju94GRgl60/gviz/tq?tqx=out:csv&sheet=PlayerDatabase'} target='_blank' rel='noreferrer'>
                                                Player ID
                                            </a>
                                            <a href={'https://www.chess.com/member/' + chessComId} target='_blank' rel='noreferrer'>{' ' + chessComId}</a>
                                        </span>
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    value={chessComId}
                                    onChange={handleChessComIdChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} lg={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    label={
                                        <span>
                                            <a href={'https://lichess.org'} target='_blank' rel='noreferrer'>
                                                Lichess.org ID
                                            </a>&nbsp;
                                            <a href={'https://lichess.org/@/' + lichessUsername} target='_blank' rel='noreferrer'>{' ' + lichessUsername}</a>
                                        </span>
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    value={lichessUsername}
                                    onChange={handleLichessUsernameChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} lg={3} >
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownload}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Download Games'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                    <Dialog open={openDialog} onClose={handleCloseDialog}>
                        <DialogTitle>Error</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                    {errorMessage}
                            </DialogContentText>
                        </DialogContent>
                            <DialogActions>
                                <Stack fullwidth m={1} direction={{ xs: "column", sm: "column", md: "row" }} spacing={1} alignItems="center">
                                    {!isSupporter &&
                                        <Link to="/SupporterDonate">
                                                <Button color="secondary" variant='contained'>Support MetaDB</Button>
                                        </Link>
                                    }
                                    <Button onClick={handleCloseDialog} color="primary" variant="contained">
                                        Close
                                    </Button>
                                </Stack>
                        </DialogActions>
                    </Dialog>
                </AccordionDetails>
            </Accordion>
        </div>
            </Box>
            );
};

export default OnlineGameDownloader;
